import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useCart } from "../contexts/CartContext";
import CustomerLayout from "../components/CustomerLayout";
import api from "../lib/api";
import { toast } from "sonner";
import { 
  Globe, Search, ExternalLink, ArrowRight,
  ShoppingCart, Plus, Check, Lock
} from "lucide-react";
import FloatingCart from "../components/FloatingCart";

// Domain sansürleme fonksiyonu
const censorDomain = (url) => {
  if (!url) return "••••••••.•••";
  try {
    const urlObj = new URL(url.startsWith("http") ? url : `https://${url}`);
    const domain = urlObj.hostname.replace("www.", "");
    const parts = domain.split(".");
    if (parts.length >= 2) {
      const name = parts.slice(0, -1).join(".");
      const ext = parts[parts.length - 1];
      // İlk ve son harfi göster, arasını sansürle
      if (name.length <= 3) {
        return `${"•".repeat(name.length)}.${ext}`;
      }
      const censored = name[0] + "•".repeat(Math.min(name.length - 2, 8)) + name[name.length - 1];
      return `${censored}.${ext}`;
    }
    return "••••••••.•••";
  } catch {
    return "••••••••.•••";
  }
};

const SiteCatalog = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { cart, addToCart, addMultipleToCart, isInCart, setCartOpen } = useCart();
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [totalSites, setTotalSites] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [languageFilter, setLanguageFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectingAll, setSelectingAll] = useState(false);
  
  // Bulk selection state
  const [selectedSites, setSelectedSites] = useState([]);
  const initialLoadDone = useRef(false);
  const lastSearch = useRef("");
  // "Tümünü seç" ile gelen minimal site verisi (id -> {id,name,url,credit_cost}).
  // Sepete eklerken ikinci bir /sites/by-ids turunu gereksiz kılar.
  const selectAllDataRef = useRef(new Map());

  // İlk yükleme - sadece 1 kere
  useEffect(() => {
    if (initialLoadDone.current) return;
    initialLoadDone.current = true;
    
    let isMounted = true;
    
    const initData = async () => {
      try {
        const response = await api.get(`/sites?page=1&limit=20`);
        if (isMounted) {
          setSites(response.data.sites || []);
          setTotalPages(response.data.pages || 1);
          setTotalSites(response.data.total || 0);
          setIsInitialized(true);
        }
      } catch (error) {
        console.error("Sites fetch error:", error);
        if (isMounted) {
          setIsInitialized(true);
        }
      }
    };
    
    initData();
    return () => { isMounted = false; };
  }, []);

  // Sayfa/filtre değişikliği için fetch
  const fetchSites = useCallback(async (searchTerm = "") => {
    if (!isInitialized) return;
    
    setLoading(true);
    try {
      let url = `/sites?page=${page}&limit=20`;
      if (categoryFilter !== "all") url += `&category=${categoryFilter}`;
      if (languageFilter !== "all") url += `&language=${languageFilter}`;
      if (searchTerm.trim()) url += `&search=${encodeURIComponent(searchTerm.trim())}`;
      
      const response = await api.get(url);
      setSites(response.data.sites || []);
      setTotalPages(response.data.pages || 1);
      setTotalSites(response.data.total || 0);
    } catch (error) {
      console.error("Sites fetch error:", error);
    } finally {
      setLoading(false);
    }
  }, [page, categoryFilter, languageFilter, isInitialized]);

  // Sayfa/filtre değiştiğinde fetch (ilk yükleme hariç)
  useEffect(() => {
    if (isInitialized && (page > 1 || categoryFilter !== "all" || languageFilter !== "all")) {
      fetchSites(search);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, categoryFilter, languageFilter]);
  
  // Debounced search - only when search actually changes
  useEffect(() => {
    if (!isInitialized) return;
    if (search === lastSearch.current) return; // Skip if same
    lastSearch.current = search;
    
    // Skip empty initial search
    if (search === "" && page === 1 && categoryFilter === "all" && languageFilter === "all") {
      return;
    }
    
    const timer = setTimeout(() => {
      if (page !== 1) {
        setPage(1);
      } else {
        fetchSites(search);
      }
    }, 500);
    
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, isInitialized]);

  // Cart add handler using context
  const handleAddToCart = (site) => {
    const success = addToCart(site);
    if (success) {
      toast.success(language === "tr" ? "Sepete eklendi" : "Added to cart");
    } else {
      toast.error(language === "tr" ? "Bu site zaten sepette" : "This site is already in cart");
    }
  };

  // Memoized categories and languages (only recalculate when sites change)
  const categories = useMemo(() => [...new Set(sites.map(s => s.category))], [sites]);
  const languages = useMemo(() => [...new Set(sites.map(s => s.language))], [sites]);

  // Search is now handled by backend, no need for client-side filtering
  const filteredSites = sites;

  // Bulk selection functions
  const toggleSelectSite = (siteId) => {
    // Sepette olan siteyi seçmeye çalışma
    if (isInCart(siteId)) {
      return;
    }
    setSelectedSites(prev => 
      prev.includes(siteId) 
        ? prev.filter(id => id !== siteId)
        : [...prev, siteId]
    );
  };

  // Tüm siteleri seç → sepete ekle → sepeti aç
  const selectAllSites = async () => {
    setSelectingAll(true);
    try {
      let url = `/sites/all-ids?with_data=1&`;
      if (categoryFilter !== "all") url += `category=${categoryFilter}&`;
      if (languageFilter !== "all") url += `language=${languageFilter}&`;
      if (search.trim()) url += `search=${encodeURIComponent(search.trim())}&`;
      
      const response = await api.get(url);
      const allIds = response.data.ids || [];
      const dataMap = selectAllDataRef.current;
      const sitesFromApi = response.data.sites || [];
      for (const s of sitesFromApi) {
        if (s && s.id) dataMap.set(s.id, s);
      }
      
      const availableIds = allIds.filter(id => !isInCart(id));

      if (allIds.length === 0) {
        setSelectedSites([]);
        toast.info(language === "tr" ? "Bu filtrelerde secilecek site bulunamadi" : "No sites found for current filters");
        return;
      }

      if (availableIds.length === 0) {
        setSelectedSites([]);
        toast.info(
          language === "tr"
            ? "Bu filtrelerdeki sitelerin tamami zaten sepette"
            : "All sites for these filters are already in cart"
        );
        setCartOpen(true);
        return;
      }

      const sitesToAdd = availableIds
        .map(id => dataMap.get(id))
        .filter(site => site && site.credit_cost !== undefined && site.credit_cost !== null);

      if (sitesToAdd.length === 0) {
        toast.error(language === "tr" ? "Sepete eklenecek site bulunamadı" : "No sites available to add");
        return;
      }

      const addedCount = addMultipleToCart(sitesToAdd);
      setSelectedSites([]);
      toast.success(
        language === "tr"
          ? `${addedCount} site sepete eklendi`
          : `${addedCount} sites added to cart`
      );
      setCartOpen(true);
    } catch (error) {
      console.error("Select all error:", error);
      toast.error(language === "tr" ? "Tüm siteleri seçerken hata oluştu" : "Error selecting all sites");
    } finally {
      setSelectingAll(false);
    }
  };

  const clearSelection = () => {
    setSelectedSites([]);
  };

  const addSelectedToCart = async () => {
    if (selectedSites.length === 0) {
      toast.error(language === "tr" ? "Site seçin" : "Select sites first");
      return;
    }
    
    // Filter out sites already in cart
    const newSiteIds = selectedSites.filter(id => !isInCart(id));
    
    if (newSiteIds.length === 0) {
      toast.error(language === "tr" ? "Seçili siteler zaten sepette" : "Selected sites already in cart");
      setSelectedSites([]);
      return;
    }

    // For sites currently visible on page, add directly
    const newIdSet = new Set(newSiteIds);
    const sitesOnPage = filteredSites.filter(site => newIdSet.has(site.id) && !isInCart(site.id));
    
    // For sites not visible (from other pages), we need their details
    const siteIdsOnPage = new Set(sitesOnPage.map(s => s.id));
    const siteIdsNotOnPage = newSiteIds.filter(id => !siteIdsOnPage.has(id));
    
    let allSitesToAdd = [...sitesOnPage];
    
    // Önce "tümünü seç" ile gelen client cache'inden karşıla (ağ turu yok)
    const dataMap = selectAllDataRef.current;
    const stillMissing = [];
    for (const id of siteIdsNotOnPage) {
      const cached = dataMap.get(id);
      if (cached) {
        allSitesToAdd.push(cached);
      } else {
        stillMissing.push(id);
      }
    }
    
    // Sadece cache'te de olmayanlar için toplu endpoint'e git (ör. manuel çok-sayfa seçim)
    if (stillMissing.length > 0) {
      toast.loading(language === "tr" ? "Site bilgileri yükleniyor..." : "Loading site details...", { id: "fetch-sites" });
      try {
        const response = await api.post("/sites/by-ids", { ids: stillMissing });
        const fetchedSites = response.data.sites || [];
        allSitesToAdd = [...allSitesToAdd, ...fetchedSites];
        toast.dismiss("fetch-sites");
      } catch (error) {
        toast.dismiss("fetch-sites");
        console.error("Error fetching site details:", error);
      }
    }
    
    // Never trim selected sites by credit here.
    // The order endpoint must decide whether the user can place all selected sites.
    const sitesToAddFiltered = allSitesToAdd.filter(site => site.credit_cost !== undefined && site.credit_cost !== null);
    const message = language === "tr"
      ? `${sitesToAddFiltered.length} site sepete eklendi`
      : `${sitesToAddFiltered.length} sites added to cart`;
    
    // Add filtered sites to cart
    if (sitesToAddFiltered.length > 0) {
      const addedCount = addMultipleToCart(sitesToAddFiltered);
      setSelectedSites([]);
      toast.success(message);
      setCartOpen(true);
    }
  };

  return (
    <>
      <CustomerLayout activeId="sites">
        <div className="p-4 md:p-6 min-w-0 overflow-x-hidden">
        <div className="max-w-6xl mx-auto min-w-0">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-mono font-bold uppercase">{language === "tr" ? "Site Kataloğu" : "Site Catalog"}</h1>
              <p className="text-muted-foreground mt-1">{language === "tr" ? "Backlink almak istediğiniz siteyi seçin" : "Select sites for backlinks"}</p>
            </div>
            {/* Cart info badge */}
            {cart.length > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/30 rounded">
                <ShoppingCart className="w-4 h-4 text-primary" />
                <span className="font-mono text-sm">{cart.length} {language === "tr" ? "site sepette" : "sites in cart"}</span>
              </div>
            )}
          </div>

          {/* Filters */}
          <div className="glass p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative md:col-span-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  placeholder={language === "tr" ? "Site ara..." : "Search sites..."}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 bg-black/50 border-white/10 rounded-none font-mono"
                  data-testid="site-search-input"
                />
              </div>
              <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v); setPage(1); }}>
                <SelectTrigger className="bg-black/50 border-white/10 rounded-none font-mono">
                  <SelectValue placeholder={language === "tr" ? "Kategori" : "Category"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === "tr" ? "Tüm Kategoriler" : "All Categories"}</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={languageFilter} onValueChange={(v) => { setLanguageFilter(v); setPage(1); }}>
                <SelectTrigger className="bg-black/50 border-white/10 rounded-none font-mono">
                  <SelectValue placeholder={language === "tr" ? "Dil" : "Language"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === "tr" ? "Tüm Diller" : "All Languages"}</SelectItem>
                  {languages.map(lang => (
                    <SelectItem key={lang} value={lang}>{lang}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Sites Grid */}
          {!isInitialized ? (
            <div className="text-center py-12">
              <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <div className="text-muted-foreground font-mono">
                {language === "tr" ? "Yükleniyor..." : "Loading..."}
              </div>
            </div>
          ) : loading ? (
            <div className="text-center py-12 text-muted-foreground font-mono">{language === "tr" ? "Güncelleniyor..." : "Updating..."}</div>
          ) : filteredSites.length === 0 ? (
            <div className="text-center py-12">
              <Globe className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground font-mono">{language === "tr" ? "Site bulunamadı" : "No sites found"}</p>
            </div>
          ) : (
            <>
              {/* Domain adresleri güvenlik gereği maskelidir (admin hariç herkes). */}
              {user?.role !== "admin" && user?.role !== "super_admin" && (
                <div className="glass p-4 mb-4 border-yellow-500/30 bg-yellow-500/5">
                  <div className="flex items-start gap-3">
                    <Lock className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-mono font-bold text-yellow-200 mb-1">
                        {language === "tr" ? "Domain Adresleri Gizli" : "Domain Addresses Hidden"}
                      </h4>
                      <p className="text-sm text-yellow-200/80">
                        {language === "tr" 
                          ? "Site ağımızı korumak için domain adresleri gizlidir. Siteleri DA/PA/SS ve trafik metriklerine göre seçip sipariş verebilirsiniz."
                          : "Domain addresses are hidden to protect our site network. You can select and order sites by their DA/PA/SS and traffic metrics."}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Bulk Selection Toolbar */}
              <div className="glass p-4 mb-4">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-primary font-mono font-bold text-lg">{totalSites}</span>
                    <span className="text-muted-foreground font-mono text-sm">
                      {language === "tr" ? "listelenen site" : "listed sites"}
                    </span>
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Tüm siteleri seç → sepete ekle → sepeti aç */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={selectAllSites}
                      disabled={selectingAll}
                      className="font-mono text-xs bg-primary/10 border-primary/30 hover:bg-primary/20"
                    >
                      {selectingAll 
                        ? (language === "tr" ? "Yükleniyor..." : "Loading...")
                        : (language === "tr" ? `Tümünü Seç (${totalSites})` : `Select All (${totalSites})`)}
                    </Button>
                    
                    {/* Seçimi temizle */}
                    {selectedSites.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearSelection}
                        className="font-mono text-xs text-muted-foreground"
                      >
                        {language === "tr" ? "Temizle" : "Clear"}
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* Seçim bilgisi ve sepete ekle */}
                {selectedSites.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
                    <span className="text-sm font-mono">
                      <span className="text-primary font-bold">{selectedSites.length}</span>
                      <span className="text-muted-foreground ml-2">{language === "tr" ? "site seçildi" : "sites selected"}</span>
                    </span>
                    <Button
                      onClick={addSelectedToCart}
                      className="font-mono text-sm uppercase bg-primary text-black rounded-none"
                    >
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      {language === "tr" ? "SEPETE EKLE" : "ADD TO CART"}
                    </Button>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 min-w-0" data-testid="sites-grid">
                {filteredSites.map((site) => {
                  const siteInCart = isInCart(site.id);
                  // Domain adresleri güvenlik gereği yalnızca admin'e açık; sansür kalıcı
                  // (ağımızı hasat eden botlara/rakibe kapalı). Backend zaten maskeli
                  // gönderir; burada da yalnızca admin gerçek domaini/tıklanır linki görür.
                  const isVerified = user?.role === "admin" || user?.role === "super_admin";
                  const displayUrl = isVerified ? site.url : censorDomain(site.url);
                  const displayName = isVerified ? site.name : (site.name?.length > 4 ? site.name.substring(0, 2) + "•".repeat(Math.min(site.name.length - 4, 6)) + site.name.substring(site.name.length - 2) : "••••");
                  
                  return (
                  <div 
                    key={site.id} 
                    className={`glass p-6 hover:border-primary/50 transition-all duration-300 group min-w-0 overflow-hidden ${selectedSites.includes(site.id) ? 'border-primary/70 bg-primary/5' : ''} ${siteInCart ? 'opacity-60' : ''}`}
                    data-testid={`site-card-${site.id}`}
                  >
                    {/* Checkbox for bulk selection */}
                    <div className="flex items-start justify-between mb-4 min-w-0 gap-2">
                      <div className="flex items-start gap-3 min-w-0 flex-1">
                        <input 
                          type="checkbox" 
                          checked={selectedSites.includes(site.id) || siteInCart}
                          onChange={() => toggleSelectSite(site.id)}
                          disabled={siteInCart}
                          className="w-4 h-4 mt-1 rounded border-white/20 bg-black/50 text-primary focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                          title={siteInCart ? (language === "tr" ? "Bu site zaten sepette" : "This site is already in cart") : ""}
                        />
                        <div className="min-w-0 flex-1 overflow-hidden">
                          <h3 className="font-mono font-bold text-lg group-hover:text-primary transition-colors flex items-center gap-2 truncate" title={displayName}>
                            <span className="truncate">{displayName}</span>
                            {!isVerified && <Lock className="w-3 h-3 text-yellow-400 flex-shrink-0" />}
                            {siteInCart && <span className="text-xs text-green-400 font-normal flex-shrink-0">({language === "tr" ? "sepette" : "in cart"})</span>}
                          </h3>
                          {isVerified ? (
                            <a 
                              href={site.url} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1 truncate block"
                              onClick={(e) => e.stopPropagation()}
                              title={site.url}
                            >
                              <span className="truncate">{site.url}</span> <ExternalLink className="w-3 h-3 flex-shrink-0" />
                            </a>
                          ) : (
                            <span className="text-sm text-muted-foreground/60 flex items-center gap-1 font-mono truncate block">
                              {displayUrl}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-4 gap-2 mb-4">
                      <div className="text-center">
                        <div className="text-xs text-muted-foreground font-mono">DA</div>
                        <div className="font-mono font-bold text-primary">{site.da}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-muted-foreground font-mono">PA</div>
                        <div className="font-mono font-bold">{site.pa}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-muted-foreground font-mono">SS</div>
                        <div className="font-mono font-bold">{site.spam_score}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-muted-foreground font-mono">{language === "tr" ? "TRAFİK" : "TRAFFIC"}</div>
                        <div className="font-mono font-bold text-sm">{site.traffic > 1000 ? `${(site.traffic/1000).toFixed(0)}K` : site.traffic}</div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="px-2 py-1 bg-white/5 text-xs font-mono">{site.category}</span>
                      <span className="px-2 py-1 bg-white/5 text-xs font-mono">{site.language}</span>
                    </div>

                    {/* Action Buttons - wrap allowed so "TEKLİ SİPARİŞ" doesn't truncate */}
                    <div className="flex flex-wrap gap-2 pt-4 border-t border-white/10">
                      <Button
                        onClick={() => handleAddToCart(site)}
                        disabled={siteInCart}
                        className="flex-1 min-w-0 font-mono text-xs uppercase bg-white/10 hover:bg-white/20 text-white rounded-none h-9 whitespace-normal"
                      >
                        {siteInCart ? (
                          <><Check className="w-3 h-3 mr-1 flex-shrink-0" /> <span className="break-words">{language === "tr" ? "SEPETTE" : "IN CART"}</span></>
                        ) : (
                          <><Plus className="w-3 h-3 mr-1 flex-shrink-0" /> <span className="break-words">{language === "tr" ? "SEPETE EKLE" : "ADD TO CART"}</span></>
                        )}
                      </Button>
                      <Button
                        onClick={() => navigate(`/order/${site.id}`)}
                        className="flex-1 min-w-0 font-mono text-xs uppercase bg-primary text-black hover:bg-primary/90 rounded-none h-9 whitespace-normal"
                      >
                        <ArrowRight className="w-3 h-3 mr-1 flex-shrink-0" />
                        <span className="break-words">{language === "tr" ? "TEKLİ SİPARİŞ" : "SINGLE ORDER"}</span>
                      </Button>
                    </div>
                  </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center gap-2 mt-8">
                  <Button 
                    variant="outline" 
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="font-mono rounded-none"
                  >
                    {language === "tr" ? "Önceki" : "Previous"}
                  </Button>
                  <span className="flex items-center px-4 font-mono text-sm text-muted-foreground">
                    {page} / {totalPages}
                  </span>
                  <Button 
                    variant="outline" 
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="font-mono rounded-none"
                  >
                    {language === "tr" ? "Sonraki" : "Next"}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      </CustomerLayout>

      {/* Floating Cart - Only shown on this page */}
      <FloatingCart />
    </>
  );
};

export default SiteCatalog;
